import React, { useEffect, useRef, useState } from "react";
import { useIdleTimer } from "./AlertsUtils/useIdleTimer";
import {
  IDLE_GRACE_MS,
  LIVE_VIEW_IDLE_MS,
} from "./AlertsUtils/liveViewIdleConfig";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import {
  StreamProtocol,
  useWhepLiveStream,
  WhepLiveStreamStatus,
} from "./AlertsUtils/useWhepLiveStream";
import { useIsTalkingToCamera } from "../../common/TalkDown/talkingCamerasStore";
import { ListenInButton, useListenInEnabled } from "./ListenInButton";
import {
  useIsHoldingListenIn,
  useListenInHoldStore,
} from "./listenInHoldStore";

const theme = createTheme({
  palette: {
    primary: {
      main: "#FF8400",
    },
  },
});

export const LIVE_VIEW_UNAVAILABLE_MESSAGE =
  "Live View not Available for this site";

export const CAMERA_OFFLINE_MESSAGE =
  "Live view for this camera is currently unavailable.";

export const LIVE_STREAM_QUOTA_REACHED_MESSAGE =
  "Monthly live stream quota has been reached for this camera.";

export const LIVE_STREAM_NOT_PERMITTED_MESSAGE =
  "Live streaming is not enabled for your account. Contact your administrator to enable it.";

export const SESSION_EXPIRED_MESSAGE =
  "Your session has expired. Reload the page to continue watching live video.";

export const STILL_WATCHING_MESSAGE = "Still watching?";
export const STILL_WATCHING_DETAIL =
  "Live view was paused after a period of inactivity to save your streaming allowance.";
export const STILL_WATCHING_COUNTDOWN = `Live view will pause in ${
  IDLE_GRACE_MS / 1000
} seconds unless you confirm you're still watching.`;
export const STILL_WATCHING_CONFIRM = "I'm still watching";

export const getLiveViewUnavailableMessage = (
  status: WhepLiveStreamStatus
): string => {
  if (status === "quota_reached") return LIVE_STREAM_QUOTA_REACHED_MESSAGE;
  if (status === "not_permitted")
    // Account-level denial (403 from the mint) — incl. deliberate suspension
    // (ENG-1548). Retrying can't help; say it's the account, not the site.
    return LIVE_STREAM_NOT_PERMITTED_MESSAGE;
  if (status === "codec_unsupported")
    // Device can't decode the camera's format (e.g. H.265 without hardware
    // support — Firefox, older machines). Distinct from a dead camera.
    return "This camera's video format isn't supported on this device. Try Chrome or Safari on newer hardware.";
  if (status === "session_expired")
    // The mint was rejected for a stale token and a refresh couldn't recover
    // it (ENG-1284) — an auth problem, not a site one.
    return SESSION_EXPIRED_MESSAGE;
  if (status === "camera_offline")
    // The session minted fine but this camera's stream never came up (or
    // died): a per-camera problem — the site-level message would mislead.
    return CAMERA_OFFLINE_MESSAGE;
  return LIVE_VIEW_UNAVAILABLE_MESSAGE;
};

interface LiveStreamPlayerProps {
  cameraId: string | null;
  enabled: boolean;
  protocol?: StreamProtocol;
  width?: number;
  height?: number;
  className?: string;
  /** Extra top offset for the LIVE badge (e.g. full-width live tab). */
  liveBadgeTopClass?: string;
}

export const LiveStreamPlayer: React.FC<LiveStreamPlayerProps> = ({
  cameraId,
  enabled,
  protocol = "hls",
  width = 540,
  height = 304,
  className = "",
  liveBadgeTopClass = "top-3",
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  // Idle prompt (ENG-1132 / ENG-2149): the clock only runs once the stream is
  // actually PLAYING (a cold start can take longer than the idle window). After
  // the window with no activity, "Still watching?" shows over the live video;
  // if nobody confirms within the grace period the hook tears the player down
  // (publish stops → metering stops) and we show the paused prompt. Resume /
  // confirm = reset the timer; Resume also flips idlePaused off, which re-runs
  // the fresh-mint play() path. One window for every viewer — staff and
  // uncapped (24/7 opt-in) plans included (ENG-2149). The player hook and the
  // timer are mutually dependent (idlePaused → hook; status → timer), so idle
  // lives in state fed from the timer below. The timer stays armed through
  // idle_paused so the pause isn't cleared out from under the hook.
  const [idle, setIdle] = useState(false);
  const { status, overageAvailable, continueWithOverage } = useWhepLiveStream(
    cameraId,
    enabled,
    videoRef,
    protocol,
    true,
    idle
  );
  // Listen-in / talk-down state (ENG-2136) — read up here because an active
  // talk-down hold or listen-in hold is engagement with the stream: it stops
  // the idle clock and dismisses a showing "Still watching?". The listen-in
  // TOGGLE deliberately does not: left on, it would keep an unattended stream
  // (and its metering) alive forever, which is what the idle prompt prevents.
  const talking = useIsTalkingToCamera(cameraId);
  const holdingListenIn = useIsHoldingListenIn(cameraId);
  const idleTimerArmed =
    enabled && (status === "playing" || status === "idle_paused");
  const idleHeld = talking || holdingListenIn;
  const {
    idle: timerIdle,
    prompting: stillWatchingPrompt,
    reset: resumeFromIdle,
  } = useIdleTimer(idleTimerArmed, LIVE_VIEW_IDLE_MS, IDLE_GRACE_MS, idleHeld);
  useEffect(() => {
    setIdle(timerIdle);
  }, [timerIdle]);

  const showVideo = enabled && status === "playing";

  // Listen-in (ENG-2136): hold-to-listen from AlertViewModeTabs + overlay
  // toggle. Half-duplex with talk-down via talkingCamerasStore. Until the
  // stream-session mint exposes has_audio on develop, treat a playing stream
  // as audio-capable so the Option A ear stays usable.
  const listenInEnabled = useListenInEnabled();
  const setHasAudio = useListenInHoldStore((s) => s.setHasAudio);
  const [toggleListening, setToggleListening] = useState(false);
  const talkingRef = useRef(talking);
  talkingRef.current = talking;
  const holdingListenInRef = useRef(holdingListenIn);
  holdingListenInRef.current = holdingListenIn;
  useEffect(() => {
    if (!showVideo) setToggleListening(false);
  }, [showVideo, cameraId]);
  useEffect(() => {
    if (cameraId == null || cameraId === "") return;
    if (!enabled || !showVideo) {
      setHasAudio(cameraId, null);
      return () => setHasAudio(cameraId, null);
    }
    setHasAudio(cameraId, true);
    return () => setHasAudio(cameraId, null);
  }, [cameraId, enabled, showVideo, setHasAudio]);
  const listening = toggleListening || holdingListenIn;
  const muted = !listening || talking;
  useEffect(() => {
    const video = videoRef.current;
    if (video && video.muted !== muted) video.muted = muted;
  }, [muted]);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onVolumeChange = () => {
      if (talkingRef.current) {
        if (!video.muted) video.muted = true;
        return;
      }
      if (holdingListenInRef.current) return;
      setToggleListening(!video.muted);
    };
    video.addEventListener("volumechange", onVolumeChange);
    return () => video.removeEventListener("volumechange", onVolumeChange);
  }, []);
  const showListenIn = showVideo && listenInEnabled;

  const showIdlePaused = enabled && status === "idle_paused";
  const showLoading =
    enabled &&
    (status === "loading" || status === "idle" || status === "starting");
  const showUnavailable =
    enabled &&
    (status === "unavailable" ||
      status === "camera_offline" ||
      status === "quota_reached" ||
      status === "not_permitted" ||
      status === "session_expired" ||
      status === "codec_unsupported");

  return (
    <div className={`relative bg-black ${className}`} style={{ width, height }}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        controls
        className="block h-full w-full object-contain"
        style={{ display: showVideo ? "block" : "none" }}
      />
      {showVideo && (
        <div
          className={`pointer-events-none absolute left-3 z-10 inline-flex items-center gap-1.5 rounded bg-[#D93636] px-2.5 py-1.5 font-mulish text-[11px] font-bold uppercase tracking-[0.08em] text-white shadow-[0_2px_8px_rgba(217,54,54,0.4)] ${liveBadgeTopClass}`}
          aria-label="Live stream"
        >
          <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-white" />
          <span>LIVE</span>
        </div>
      )}
      {showListenIn && (
        <div className="absolute bottom-12 right-3 z-30">
          <ListenInButton
            listening={toggleListening || holdingListenIn}
            talking={talking}
            onToggle={() => setToggleListening((prev) => !prev)}
          />
        </div>
      )}
      {showVideo && stillWatchingPrompt && (
        // Centred card under the listen-in control (z-30), so it stays
        // clickable while the prompt shows.
        <div
          className="absolute left-1/2 top-1/2 z-20 flex w-[calc(100%-24px)] max-w-[360px] -translate-x-1/2 -translate-y-1/2 flex-col items-center rounded bg-black/85 px-4 py-3 shadow-lg"
          data-testid="live-still-watching-prompt"
          role="alertdialog"
          aria-label={STILL_WATCHING_MESSAGE}
        >
          <p className="font-mulish text-center text-[16px] font-bold text-white">
            {STILL_WATCHING_MESSAGE}
          </p>
          <p className="font-mulish mt-1 text-center text-[12px] text-white/80">
            {STILL_WATCHING_COUNTDOWN}
          </p>
          <button
            type="button"
            onClick={resumeFromIdle}
            className="font-mulish mt-2 rounded bg-[#FF8400] px-4 py-1.5 text-[13px] font-bold text-white hover:bg-[#e67700]"
          >
            {STILL_WATCHING_CONFIRM}
          </button>
        </div>
      )}
      {showIdlePaused && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center bg-black px-4"
          data-testid="live-idle-prompt"
        >
          <p className="font-mulish text-center text-[20px] font-bold text-white">
            {STILL_WATCHING_MESSAGE}
          </p>
          <p className="font-mulish mt-2 text-center text-[14px] text-white/80">
            {STILL_WATCHING_DETAIL}
          </p>
          <button
            type="button"
            onClick={resumeFromIdle}
            className="font-mulish mt-4 rounded bg-[#FF8400] px-4 py-2 text-[14px] font-bold text-white hover:bg-[#e67700]"
          >
            Resume
          </button>
        </div>
      )}
      {(showLoading || showUnavailable) && (
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4">
          {showLoading ? (
            <>
              <ThemeProvider theme={theme}>
                <CircularProgress color="primary" />
              </ThemeProvider>
              <p className="font-mulish mt-4 text-center text-[16px] text-white">
                {status === "starting"
                  ? "Starting camera stream…"
                  : "Connecting live view..."}
              </p>
            </>
          ) : status === "quota_reached" && overageAvailable ? (
            // Soft cap (ENG-721): the mint offered overage. One explicit
            // click is the billing consent — the hook re-mints with
            // overage_opt_in and its keepalives carry it for this view.
            <>
              <p className="font-mulish text-center text-[18px] text-white">
                {LIVE_STREAM_QUOTA_REACHED_MESSAGE}
              </p>
              <p className="font-mulish mt-2 text-center text-[14px] text-white/80">
                You can keep streaming this camera — additional charges will
                apply to your account.
              </p>
              <button
                type="button"
                onClick={continueWithOverage}
                className="font-mulish mt-4 rounded bg-[#FF8400] px-4 py-2 text-[14px] font-bold text-white hover:bg-[#e67700]"
              >
                Continue streaming
              </button>
            </>
          ) : (
            <p className="font-mulish text-center text-[18px] text-white">
              {getLiveViewUnavailableMessage(status)}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export type { WhepLiveStreamStatus };
